import { Download } from '@mui/icons-material';
import { Box, Button, Divider, Stack, Typography } from '@mui/material';
import { cloneDeep, concat, get, set } from 'lodash';
import React, { FC } from 'react';
import CreateForm from 'src/components/ui-component/form-component/create-form/CreateForm';
import CreateTable from 'src/components/ui-component/form-component/create-form/CreateTable';
import MUIAlert from 'src/components/ui-component/mui-alert/MUIAlert';
import { useApplication } from 'src/context/ApplicationContext';
import { KMU_PATHS } from 'src/lib/constants/componentsPath';
import {
	ALS_ERLEDIGT_MARKIEREN_LABEL,
	ANTRAGS_FORMULAREN,
	EINSCHAETZUNG_ALS_UNTERNEHMEN,
	EINSCHAETZUNG_NICHT_KMU,
	FORM_FIELDS_TYPES,
	KMU_SELECTIONS,
	UNTERNEHMEN_ERFUELLT_KRITERIEN,
	V_PARTNER_KEINE_ANGABEN_TEXT,
} from 'src/lib/constants/constant';
import { PUBLIC_DOCUMENTS_PATHS } from 'src/lib/constants/publicDocumentPaths';
import { FormStrucItem, Validation } from 'src/lib/types/types';
import { ForceTrue, FullYear, NonRequired, Required } from 'src/lib/validation/ValidationTypes';
import { InhaltTrenner, InhaltWrapper } from 'src/pages/private/online-antrag/antrag-components/Wrapper';
import { checkIfPruefAssitenzNeedsToValidate } from 'src/pages/private/online-antrag/application-helper-function/AppHelperfunctions';
import { addIndexesToPath, checkReadOnlyInUeberarbeitung, handleDownloadFile, isTrue } from 'src/utils/helper';
import { DownloadSelbstErklaerung } from '../../../../DownloadAntragPDF';
import PruefAssistenz from '../../../../antrag-components/PruefAssistenz';
import PostVersandInfo from './PostVersandInfo';
import UploadKmuHinweise from './UploadKmuHinweise';

// import schemas
import AntragSchema from 'src/lib/paths/antrag_FULL_Paths.json';
const P = AntragSchema.attributes;

const AntragstellerKmuErklaerungGlobal: FC<{ unternehmenId: number; unternehmenName: string; positionId?: string; hasComment?: boolean }> = ({
	unternehmenId,
	positionId,
	unternehmenName,
	hasComment,
}) => {
	const { application, setApplicationProperty, readOnly, saveApplication } = useApplication();
	const updatedReadOnly = checkReadOnlyInUeberarbeitung(application, readOnly, hasComment);

	const handleUpdateErklaerungDaten = (key: string, value: any, kmuIndex: number) => {
		let appCopy = cloneDeep(application);
		set(appCopy, key, value);
		const unternehmenPath = addIndexesToPath(
			P.Selbsterklaerung_KMU.schema.attributes.Kmu_Unternehmen.path,
			[`${kmuIndex}`],
			['Selbsterklaerung_KMU'],
		);
		if (get(appCopy, `${unternehmenPath}.id`, -1) > 0) {
			// DO Nothing, since the unternehmen ID already exist
		} else {
			set(appCopy, unternehmenPath, unternehmenId);
		}

		setApplicationProperty('attributes', get(appCopy, 'attributes'));
	};

	const handleGenerateSelbstErklaerung = async () => {
		DownloadSelbstErklaerung(application, unternehmenId, unternehmenName, positionId, readOnly, saveApplication);
	};

	const antragsformular = get(application, P.Allgemein_Antragsformular.path, '');
	const isLoewe = antragsformular === ANTRAGS_FORMULAREN.Loewe;
	if (unternehmenId) {
		return (
			<>
				{get(application, P.Selbsterklaerung_KMU.path, []).map((ele: any, i: number) => {
					if (get(ele, `${KMU_PATHS.Unternehmen}.id`, -1) === unternehmenId || get(ele, KMU_PATHS.PositionID, '') === positionId) {
						return (
							<React.Fragment key={i}>
								<PruefAssistenz
									statusPath={addIndexesToPath(
										P.Selbsterklaerung_KMU.schema.attributes.Kmu_Pruefstatus.path,
										[`${i}`],
										['Selbsterklaerung_KMU'],
									)}
									hinweisPath={addIndexesToPath(
										P.Selbsterklaerung_KMU.schema.attributes.Kmu_Hinnweis_Antragsteller.path,
										[`${i}`],
										['Selbsterklaerung_KMU'],
									)}
									ueberarbeitetAmPath={addIndexesToPath(
										P.Selbsterklaerung_KMU.schema.attributes.Kmu_Ueberarbeitet_Am.path,
										[`${i}`],
										['Selbsterklaerung_KMU'],
									)}
									erledigtPath={addIndexesToPath(
										P.Selbsterklaerung_KMU.schema.attributes.Kmu_Pruefung_Erledigt.path,
										[`${i}`],
										['Selbsterklaerung_KMU'],
									)}
									formStruc={mapFormStruc(formStrucPruefAssistenz(), i)}
								/>

								{/**
								 * KMU Verzischt
								 */}
								<InhaltWrapper>
									<Box>
										<Typography variant='h4'>KMU-Erklärung: {unternehmenName}</Typography>
										<Divider />
									</Box>
									<Typography variant={'h5'} component={'p'} sx={{ fontWeight: 600 }}>
										Einschätzung als Unternehmen
									</Typography>
									<Typography>
										Die Einstufung als KMU setzt zunächst die Einschätzung als Unternehmen voraus. Bitte geben Sie hier an, was auf Sie
										zutrifft.
									</Typography>
									<Typography>
										Im Sinne der Empfehlung der Kommission vom 6. Mai 2003 betreffend die Definition der Kleinstunternehmen sowie der
										kleinen und mittleren Unternehmen (2003/361/EG), veröffentlicht im Amtsblatt der Europäischen Union L 124 vom 20. Mai
										2003, S. 36 trifft folgendes auf o.g. Antragsteller / Projektpartner zu:
									</Typography>

									{get(ele, KMU_PATHS.Einschaetzung_Als_Unternehmen, '') === EINSCHAETZUNG_ALS_UNTERNEHMEN.keinUnternehmen && (
										<MUIAlert message={'Es sind keine weiteren Angaben zu machen, eine KMU-Selbsterklärung entfällt.'} />
									)}
									{get(ele, KMU_PATHS.Einschaetzung_Als_Unternehmen, '') === EINSCHAETZUNG_ALS_UNTERNEHMEN.KeinKMU && !isLoewe && (
										<MUIAlert message={'Es sind keine weiteren Angaben zu machen, eine KMU-Selbsterklärung entfällt.'} />
									)}
								</InhaltWrapper>

								<CreateForm
									formStruc={mapFormStruc(formStrucVerzicht(), i)}
									formValues={application}
									updateFormValuesFunc={(key, value) => handleUpdateErklaerungDaten(key, value, i)}
									readOnly={updatedReadOnly}
								/>
								{isLoewe && get(ele, KMU_PATHS.Einschaetzung_Als_Unternehmen, '') === EINSCHAETZUNG_ALS_UNTERNEHMEN.KeinKMU && (
									<>
										<InhaltTrenner />
										<InhaltWrapper>
											<Typography variant={'h5'} component={'p'} sx={{ fontWeight: 600 }}>
												Unternehmenseinschätzung für nicht KMUs
											</Typography>
											<Typography>
												Für die Antragseinreichung sind auch für Unternehmen, die kein KMU sind, weiterführende Informationen anzugeben.
												Bitte geben Sie hier an, was auf Ihr Unternehmen zutrifft:{' '}
											</Typography>
											{get(ele, KMU_PATHS.Unternehmenseinschaetzung_Fuer_Nicht_Kmus, '') ===
												EINSCHAETZUNG_NICHT_KMU.inhaberPersonGefuehrtesUnternehmen && (
												<MUIAlert message={'Bitte erstellen Sie eine KMU-Selbsterklärung für Ihr Unternehmen!'} />
											)}
											{get(ele, KMU_PATHS.Unternehmenseinschaetzung_Fuer_Nicht_Kmus, '') ===
												EINSCHAETZUNG_NICHT_KMU.familiengefuehrtesUnternehmen && (
												<MUIAlert message={'Bitte erstellen Sie eine KMU-Selbsterklärung für Ihr Unternehmen!'} />
											)}
											{get(ele, KMU_PATHS.Unternehmenseinschaetzung_Fuer_Nicht_Kmus, '') ===
												EINSCHAETZUNG_NICHT_KMU.unternehmenMitMehrAls1000MA && (
												<MUIAlert message={'Es sind keine weiteren Angaben zu machen, eine KMU-Selbsterklärung entfällt.'} />
											)}
											<CreateForm
												formStruc={mapFormStruc(formStrucNichtKmu(), i)}
												formValues={application}
												updateFormValuesFunc={(key, value) => handleUpdateErklaerungDaten(key, value, i)}
												readOnly={updatedReadOnly}
											/>
										</InhaltWrapper>
									</>
								)}

								{(get(ele, KMU_PATHS.Einschaetzung_Als_Unternehmen, '') === EINSCHAETZUNG_ALS_UNTERNEHMEN.KMU ||
									(get(ele, KMU_PATHS.Einschaetzung_Als_Unternehmen, '') === EINSCHAETZUNG_ALS_UNTERNEHMEN.KeinKMU &&
										(get(ele, KMU_PATHS.Unternehmenseinschaetzung_Fuer_Nicht_Kmus, '') ===
											EINSCHAETZUNG_NICHT_KMU.inhaberPersonGefuehrtesUnternehmen ||
											get(ele, KMU_PATHS.Unternehmenseinschaetzung_Fuer_Nicht_Kmus, '') ===
												EINSCHAETZUNG_NICHT_KMU.familiengefuehrtesUnternehmen))) && (
									<>
										<InhaltTrenner />
										<InhaltWrapper>
											<Typography variant={'h5'} component={'p'} sx={{ fontWeight: 600 }}>
												Angabe des Unternehmenstyps
											</Typography>
											<Typography>Bitte geben Sie hier an, welche Aussage auf Ihr Unternehmen zutrifft:</Typography>
										</InhaltWrapper>
										<CreateForm
											formStruc={mapFormStruc(formStrucUnternehmensTyp(), i)}
											formValues={application}
											updateFormValuesFunc={(key, value) => handleUpdateErklaerungDaten(key, value, i)}
											readOnly={updatedReadOnly}
										/>
										<InhaltTrenner />
										<InhaltWrapper>
											<Typography variant={'h5'} component={'p'} sx={{ fontWeight: 600 }}>
												KMU-Selbsterklärung Berechnungen
											</Typography>

											<Typography>
												Um die genaue Größe Ihres Unternehmens zu bestimmen, sind weitere Angaben und Berechnungen erforderlich.
											</Typography>
											<Typography>
												Für diese Berechnungen haben wir Ihnen eine Excel-Vorlage mit detaillierten Erläuterungen erstellt. Die Angaben,
												die sich in dem Dokument errechnen, werden für die weiteren Eingaben im Portal benötigt.
											</Typography>
											<Typography>
												Bitte laden Sie hier das Dokument „KMU-Selbsterklärung-Berechnung“ (Excel-Datei) herunter, füllen Sie es
												vollständig aus und speichern Sie es anschließend als pdf-Datei:
											</Typography>

											<Button
												variant='outlined'
												startIcon={<Download />}
												sx={{ maxWidth: 'fit-content' }}
												onClick={() => handleDownloadFile(PUBLIC_DOCUMENTS_PATHS.antragsUnterlagen.selbstErklaerungKMU)}
											>
												KMU-Selbsterklärung-Berechnung
											</Button>
										</InhaltWrapper>
										<InhaltTrenner />
										<InhaltWrapper>
											<Typography variant={'h5'} component={'p'} sx={{ fontWeight: 600 }}>
												Unternehmenskennzahlen
											</Typography>

											<Typography>
												In diesem Schritt benötigen Sie die von Ihnen ausgefüllte Excel-Tabelle „KMU-Selbsterklärung-Berechnung“.{' '}
											</Typography>
											<Typography>
												Bitte übertragen Sie die für Ihr Unternehmen berechneten Ergebnisse, die Sie unter Punkt 5 finden, in das
												Formular.
											</Typography>
											<Typography>
												<strong>Für Start-ups:</strong> Bitte die Daten soweit möglich ausfüllen, ggf. die Inhalte für das aktuelle bzw.
												vorangegangene Jahr auf "0" setzen, falls noch keine Aktivitäten stattgefunden haben.
											</Typography>
											<Typography>
												<strong>Bei abweichendem Geschäftsjahr:</strong> Wenn das Geschäftsjahr vom Kalenderjahr abweicht, bitte als
												Bezugsjahr das Ende des Geschäftsjahres nehmen.
											</Typography>
										</InhaltWrapper>
										<CreateForm
											formStruc={mapFormStruc(formStrucKennzahlen(), i)}
											formValues={application}
											updateFormValuesFunc={(key, value) => handleUpdateErklaerungDaten(key, value, i)}
											readOnly={updatedReadOnly}
										/>
										<Typography variant={'body2'} sx={{ mt: 1 }}>
											* Die Schwellenwerte beziehen sich auf den letzten durchgeführten Jahresabschluss. Es müssen Daten aus zwei
											aufeinanderfolgenden Jahren dokumentiert werden. Diese Jahre müssen in den letzten drei Jahren mit durchgeführtem
											Jahresabschluss vor dem Bewilligungsjahr liegen.
										</Typography>
										<Box sx={{ mt: '3rem' }}>
											<InhaltWrapper>
												<Typography variant='h5' sx={{ fontWeight: 600 }}>
													Bitte geben Sie hier an, welche Aussagen auf Ihr Unternehmen zutreffen:
												</Typography>
											</InhaltWrapper>
											<Typography sx={{ fontWeight: 600, mt: '1rem' }}>Keine Fusion oder Übernahme:</Typography>
											<CreateForm
												formStruc={mapFormStruc(formStrucErklaerungenFuesion(), i)}
												formValues={application}
												updateFormValuesFunc={(key, value) => handleUpdateErklaerungDaten(key, value, i)}
												readOnly={updatedReadOnly}
											/>

											<Box sx={{ mt: '1rem' }}>
												<CreateForm
													formStruc={mapFormStruc(formStrucErklaerungenKontrolle(), i)}
													formValues={application}
													updateFormValuesFunc={(key, value) => handleUpdateErklaerungDaten(key, value, i)}
													readOnly={updatedReadOnly}
												/>
											</Box>
											<Typography variant='body2' sx={{ mt: '1rem', mb: '1rem' }}>
												**Siehe Artikel 3 Nr. 4 des Anhangs der Empfehlung 2003/361/EG.{' '}
											</Typography>

											<CreateForm
												formStruc={mapFormStruc(formStrucErklaerungenKapital(), i)}
												formValues={application}
												updateFormValuesFunc={(key, value) => handleUpdateErklaerungDaten(key, value, i)}
												readOnly={updatedReadOnly}
											/>
										</Box>
										<InhaltTrenner />
										<Box sx={{ mt: '2rem' }}>
											{/* <InhaltWrapper>
												<Typography variant={'h5'} component={'p'} sx={{ fontWeight: 600 }}>
													Das Unternehmen erfüllt die Kriterien eines
												</Typography> 
											</InhaltWrapper> */}
											<CreateForm
												formStruc={mapFormStruc(formStrucKriterien(application), i)}
												formValues={application}
												updateFormValuesFunc={(key, value) => handleUpdateErklaerungDaten(key, value, i)}
												readOnly={updatedReadOnly}
											/>
										</Box>

										<CreateForm
											formStruc={mapFormStruc(formStrucUmsatz(), i)}
											formValues={application}
											updateFormValuesFunc={(key, value) => handleUpdateErklaerungDaten(key, value, i)}
											readOnly={updatedReadOnly}
										/>

										<InhaltTrenner />

										<Box sx={{ mt: '2rem' }}>
											<InhaltWrapper>
												<Typography variant={'h5'} component={'p'} sx={{ fontWeight: 600 }}>
													Download der KMU-Selbsterklärung
												</Typography>
												<Typography>
													Bitte laden Sie hier Ihre vollständig ausgefüllte KMU-Selbsterklärung zur Unterzeichnung herunter.
												</Typography>
												<Stack direction={'row'} spacing={2}>
													<Button
														variant='outlined'
														startIcon={<Download />}
														sx={{ maxWidth: 'fit-content' }}
														onClick={() => handleGenerateSelbstErklaerung()}
													>
														KMU-Selbsterklärung für {unternehmenName}
													</Button>
												</Stack>
												<Typography>
													<strong>Bitte beachten Sie:</strong> Die Unterzeichnung der KMU-Selbsterklärung muss per eigenhändiger
													Unterschrift erfolgen. Das Einfügen einer Bilddatei o.Ä. als Unterschrift ist nicht zulässig. Bitte speichern
													Sie die unterzeichnete Erklärung anschließend als pdf- Datei ab.
												</Typography>
											</InhaltWrapper>
										</Box>
										<InhaltTrenner />

										<InhaltWrapper>
											<Typography variant={'h5'} component={'p'} sx={{ fontWeight: 600 }}>
												Upload der KMU-Selbsterklärung
											</Typography>
											<Typography>
												Bitte laden Sie hier die unterzeichnete KMU-Selbsterklärung und das Dokument KMU-Selbsterklärung-Berechnung als
												pdf-Datei hoch:
											</Typography>

											<UploadKmuHinweise antragsFormular={get(application, P.Allgemein_Antragsformular.path, '')} />

											<CreateTable
												formStruc={mapFormStruc(formStrucUploadErklaerung(), i)}
												formValues={application}
												updateFormValuesFunc={(key, value) => handleUpdateErklaerungDaten(key, value, i)}
												readOnly={updatedReadOnly}
												columnsLabel={['Bezeichnungs des Dokuments', 'Datei']}
											/>
										</InhaltWrapper>

										<PostVersandInfo application={application} />
									</>
								)}
							</React.Fragment>
						);
					}
					return null;
				})}
			</>
		);
	} else {
		return <MUIAlert message={V_PARTNER_KEINE_ANGABEN_TEXT} />;
	}
};

export default AntragstellerKmuErklaerungGlobal;

const formStrucPruefAssistenz = () => {
	let struc: FormStrucItem[] = [
		{
			fields: [
				{
					...P.Selbsterklaerung_KMU.schema.attributes.Kmu_Pruefung_Erledigt,
					label: ALS_ERLEDIGT_MARKIEREN_LABEL,
					validation: [{ validateFunction: ForceTrue }],
				},
			],
		},
	];

	return struc;
};
const formStrucVerzicht = () => {
	let struc: FormStrucItem[] = [
		{
			fields: [
				{
					...P.Selbsterklaerung_KMU.schema.attributes.Einschaetzung_Als_Unternehmen,
					label: '',
					type: FORM_FIELDS_TYPES.radio,
					direction: 'column',
				},
			],
			userProps: { columnToDivide: 1 },
		},
	];
	return struc;
};

const formStrucNichtKmu = () => {
	let struc: FormStrucItem[] = [
		{
			fields: [
				{
					...P.Selbsterklaerung_KMU.schema.attributes.Unternehmenseinschaetzung_Fuer_Nicht_Kmus,
					label: '',
					type: FORM_FIELDS_TYPES.radio,
					direction: 'column',
				},
			],
			userProps: { columnToDivide: 1 },
		},
	];
	return struc;
};

const formStrucUnternehmensTyp = () => {
	let struc: FormStrucItem[] = [
		{
			fields: [
				{
					...P.Selbsterklaerung_KMU.schema.attributes.Kmu_Unternehmenstyp,
					type: FORM_FIELDS_TYPES.radio,
					label: '',
					direction: 'column',
					display: 'accordion',
					descriptions: descriptions,
				},
			],
			userProps: { columnToDivide: 1 },
		},
	];
	return struc;
};
const formStrucKennzahlen = () => {
	let struc: FormStrucItem[] = [
		{
			fields: [
				{
					...P.Selbsterklaerung_KMU.schema.attributes.Kmu_Kennzahlen,
					type: FORM_FIELDS_TYPES.editableTable,
					maxLevel: '2',
					fieldProps: { Kmu_Kennzahlen_Jahresumsatz: { format: 'currency' }, Kmu_Kennzahlen_Jahresbilanzsumme: { format: 'currency' } },
				},
			],
			userProps: { columnToDivide: 1 },
		},
	];
	return struc;
};
const formStrucErklaerungenFuesion = () => {
	let struc: FormStrucItem[] = [
		{
			fields: [
				{
					...P.Selbsterklaerung_KMU.schema.attributes.Kmu_Aussagen_Keine_Fusion_Oder_Uebernahme,
					direction: 'column',
					type: FORM_FIELDS_TYPES.checkbox,
				},
			],
			userProps: { direction: 'column', columnToDivide: 1 },
		},
	];
	return struc;
};
const formStrucErklaerungenKontrolle = () => {
	let struc: FormStrucItem[] = [
		{
			fields: [
				{ ...P.Selbsterklaerung_KMU.schema.attributes.Kmu_Aussagen_Erklaerung_Zur_Kontrolle, direction: 'column', type: FORM_FIELDS_TYPES.radio },
			],
			userProps: { direction: 'column', columnToDivide: 1 },
		},
	];
	return struc;
};
const formStrucErklaerungenKapital = () => {
	let struc: FormStrucItem[] = [
		{
			fields: [
				{ ...P.Selbsterklaerung_KMU.schema.attributes.Kmu_Aussagen_Unklare_Kapitalstreuung, direction: 'column', type: FORM_FIELDS_TYPES.radio },
			],
			userProps: { direction: 'column', columnToDivide: 1 },
		},
	];
	return struc;
};
const formStrucKriterien = (application: any) => {
	const antragsformular = get(application, P.Allgemein_Antragsformular.path, '');
	const isLoewe = antragsformular === ANTRAGS_FORMULAREN.Loewe;
	const loeweEnumValues = [
		UNTERNEHMEN_ERFUELLT_KRITERIEN.familiengefuehrtesUnternehmen,
		UNTERNEHMEN_ERFUELLT_KRITERIEN.inhaberPersonGefuehrtesUnternehmen,
	];
	const unternehmenErfuelltKriterienEnum = P.Selbsterklaerung_KMU.schema.attributes.Unternehmen_Erfuellt_Die_Kriterien_Eines.enum;
	const filteredEnum = unternehmenErfuelltKriterienEnum.filter((value) => !loeweEnumValues.includes(value));
	let struc: FormStrucItem[] = [
		{
			fields: [
				{
					...P.Selbsterklaerung_KMU.schema.attributes.Unternehmen_Erfuellt_Die_Kriterien_Eines,
					enum: isLoewe ? unternehmenErfuelltKriterienEnum : filteredEnum,
					type: FORM_FIELDS_TYPES.radio,
					direction: 'column',
					label: 'Das Unternehmen erfüllt die Kriterien eines',
					// hideLabel: true,
				},
			],
			userProps: { direction: 'column', columnToDivide: 1 },
		},
	];
	return struc;
};
const formStrucUmsatz = () => {
	const anzalMitarbeiterEnum = P.Selbsterklaerung_KMU.schema.attributes.Kmu_Anzahl_Der_Mitarbeiter.enum;
	const customAnzahlMitarbeiterSortOrder = [
		KMU_SELECTIONS.Institutionstyp.KleineUnternehmen.AnzahlMitarbeiter.value,
		KMU_SELECTIONS.Institutionstyp.MittlereUnternehmen.AnzahlMitarbeiter.value,
		KMU_SELECTIONS.Institutionstyp.Grossunternehmen.AnzahlMitarbeiter.value,
	];
	const sortedAnzahlMitarbeiterEnum = [
		...customAnzahlMitarbeiterSortOrder.filter((value) => anzalMitarbeiterEnum.includes(value)),
		...anzalMitarbeiterEnum.filter((value) => !customAnzahlMitarbeiterSortOrder.includes(value)),
	];
	let struc: FormStrucItem[] = [
		{
			fields: [
				{ ...P.Selbsterklaerung_KMU.schema.attributes.Kmu_Anzahl_Der_Mitarbeiter, enum: sortedAnzahlMitarbeiterEnum, sortAlphabetically: false },
				P.Selbsterklaerung_KMU.schema.attributes.Kmu_Jahresumsatz,
				P.Selbsterklaerung_KMU.schema.attributes.Kmu_Jahresbilanzsumme,
			],
		},
	];
	return struc;
};
const formStrucUploadErklaerung = () => {
	let struc: FormStrucItem[] = [
		{
			fields: [
				{ path: 't.t', type: 'label', label: 'KMU-Selbsterklärung', validation: [{ validateFunction: NonRequired }] },
				{ ...P.Selbsterklaerung_KMU.schema.attributes.Kmu_Unterschrieben_Datei, allowedFileNameKeywords: ['KMU-Selbsterklärung'] },
			],
			// userProps: { direction: 'column', columnToDivide: 2 },
		},
		{
			fields: [
				{ path: 't.t', type: 'label', label: 'KMU-Selbsterklärung-Berechnung', validation: [{ validateFunction: NonRequired }] },
				P.Selbsterklaerung_KMU.schema.attributes.Kmu_Berechnung_Datei,
			],
			userProps: { direction: 'column', columnToDivide: 2 },
		},
	];
	return struc;
};

const mapFormStruc = (formStruc: FormStrucItem[], index: number) => {
	let result = formStruc.map((row) => {
		return {
			...row,
			fields: (get(row, 'fields', []) as Array<any>).map((value) => {
				return {
					...value,
					path: addIndexesToPath(get(value, 'path', ''), [`${index}`], ['Selbsterklaerung_KMU']),
				};
			}),
		};
	});
	return result;
};

const mapFormStrucKennzahlen = (formStruc: FormStrucItem[], index: number, secondIndex: number) => {
	let result = formStruc.map((row) => {
		return {
			...row,
			fields: (get(row, 'fields', []) as Array<any>).map((value) => {
				return {
					...value,
					path: addIndexesToPath(get(value, 'path', ''), [`${index}`, `${secondIndex}`], ['Selbsterklaerung_KMU', 'Kmu_Kennzahlen']),
				};
			}),
		};
	});
	return result;
};

const customFormStrucKennzahlen = (application: any, _bezugsjahr: number | undefined, index: number): FormStrucItem[] => {
	/*
	 * **********************************************************************************************************************************************************
	 * ****************************************** !!!!!! ZUERST LESEN !!!!!!! ************************************
	 * ********************* Erstmal alle Validierung außer Required-Feld ausschalten.
	 * ********************* Dies wird aber nachher genau definiert.
	 * ********************* Bis dahin erstmal auskommentiert lassen
	 * **********************************************************************************************************************************************************
	 */
	// const errorMinMessage =
	// 	'Achtung: Für den ausgewählten Institutionstypen unter Antragsteller/in konnte kein minimaler Vergleichswert ermittelt werden. Bitte überprüfen Sie ob ein Institutionstyp ausgewählt wurde oder wenden Sie sich an die Entwickler, wenn das Problem weiterhin besteht.';
	// const errorMaxMessage =
	// 	'Achtung: Für den ausgewählten Institutionstypen unter Antragsteller/in konnte kein maximaler Vergleichswert ermittelt werden. Bitte überprüfen Sie ob ein Institutionstyp ausgewählt wurde oder wenden Sie sich an die Entwickler, wenn das Problem weiterhin besteht.';
	// const errorSelectedMinMessage =
	// 	'Es konnte kein minimaler Vergleichswert ermittelt werden. Bitte überprüfen Sie ob alle Auswahlfelder auf der Seite ausgewählt wurden.';
	// const errorSelectedMaxMessage =
	// 	'Es konnte kein maximaler Vergleichswert ermittelt werden. Bitte überprüfen Sie ob alle Auswahlfelder auf der Seite ausgewählt wurden.';
	// const currentYear = new Date().getFullYear();
	// const minyear = new Date(get(application, P.Antragstellende_Organisation_Gruendungsdatum.path)).getFullYear();
	let partnerInstitutionsTyp: string | undefined = undefined;
	let vbPartnerPositionIdPath = addIndexesToPath(P.Selbsterklaerung_KMU.schema.attributes.Position_ID.path, [`${index}`], ['Selbsterklaerung_KMU']);
	const positionId = get(application, vbPartnerPositionIdPath);

	if (positionId) {
		const partner = get(application, P.Verbundpartner.path).find((partner: any) => partner.Position_ID === positionId);
		partnerInstitutionsTyp = get(partner, 'Verbundpartner_Institutionstyp');
	}

	// const selectedAnzahlMitarbeiterPath = addIndexesToPath(
	// 	P.Selbsterklaerung_KMU.schema.attributes.Kmu_Anzahl_Der_Mitarbeiter.path,
	// 	[`${index}`],
	// 	['Selbsterklaerung_KMU']
	// );
	// const selectedJahresBilanzPath = addIndexesToPath(
	// 	P.Selbsterklaerung_KMU.schema.attributes.Kmu_Jahresbilanzsumme.path,
	// 	[`${index}`],
	// 	['Selbsterklaerung_KMU']
	// );
	// const selectedJahresUmsatzPath = addIndexesToPath(
	// 	P.Selbsterklaerung_KMU.schema.attributes.Kmu_Jahresumsatz.path,
	// 	[`${index}`],
	// 	['Selbsterklaerung_KMU']
	// );
	// const selectedJahresUmsatz = get(application, selectedJahresUmsatzPath);
	// const minMaxJahresUmsatz = getSelectedMinMaxKMU(selectedJahresUmsatz);
	// const selectedAnzahlMitarbeiter = get(application, selectedAnzahlMitarbeiterPath);
	// const minMaxAnzahlMitarbeiter = getSelectedMinMaxKMU(selectedAnzahlMitarbeiter);
	// const selectedJahresBilanz = get(application, selectedJahresBilanzPath);
	// const minMaxJahresBilanz = getSelectedMinMaxKMU(selectedJahresBilanz);
	const institutionstyp =
		partnerInstitutionsTyp ? partnerInstitutionsTyp : get(application, P.Antragstellende_Organisation_Institutionstyp_Des_Antragstellers.path);
	const unternehmen = Object.values(KMU_SELECTIONS.Institutionstyp).find((unternehmen) => unternehmen.value === institutionstyp);

	let mitarbeiterzahlValidation: Validation[] =
		unternehmen ?
			[
				{ validateFunction: Required },
				// { validateFunction: GetMinIntValidation, params: [unternehmen?.AnzahlMitarbeiter.min, errorMinMessage] },
				// //ts: max not always available
				// ...(get(unternehmen?.AnzahlMitarbeiter, 'max') !== undefined ?
				// 	[{ validateFunction: GetMaxIntValidation, params: [get(unternehmen?.AnzahlMitarbeiter, 'max'), errorMaxMessage] }]
				// :	[]),
			]
		:	[
				{ validateFunction: Required },
				// { validateFunction: GetMinIntValidation, params: [minMaxAnzahlMitarbeiter?.property?.min, errorSelectedMinMessage] },
				// ...(!selectedAnzahlMitarbeiter || get(minMaxAnzahlMitarbeiter?.property, 'max') !== undefined ?
				// 	[{ validateFunction: GetMaxIntValidation, params: [get(minMaxAnzahlMitarbeiter?.property, 'max'), errorSelectedMaxMessage] }]
				// :	[{ validateFunction: NonNegativeIntValidation }]),
			];
	let jahresumsatzValidation: Validation[] =
		unternehmen ?
			[
				{ validateFunction: Required },
				// { validateFunction: GetMinDecimalValidation, params: [unternehmen?.JahresUmsatz.min, '', errorMinMessage] },
				// //ts: max not always available
				// ...(get(unternehmen?.JahresUmsatz, 'max') !== undefined ?
				// 	[{ validateFunction: GetMaxDecimalValidation, params: [get(unternehmen?.JahresUmsatz, 'max'), '', errorMaxMessage] }]
				// :	[]),
			]
		:	[
				{ validateFunction: Required },
				// { validateFunction: GetMinDecimalValidation, params: [minMaxJahresUmsatz?.property?.min, '', errorSelectedMinMessage] },
				// ...(!selectedJahresUmsatz || get(minMaxJahresUmsatz?.property, 'max') !== undefined ?
				// 	[{ validateFunction: GetMaxDecimalValidation, params: [get(minMaxJahresUmsatz?.property, 'max'), '', errorSelectedMaxMessage] }]
				// :	[{ validateFunction: NonNegativeDecimalValidation }]),
			];

	let jahresBilanzValidation: Validation[] =
		unternehmen ?
			[
				{ validateFunction: Required },
				// { validateFunction: GetMinDecimalValidation, params: [unternehmen?.JahresBilanzSumme.min, '', errorMinMessage] },
				// //ts: max not always available
				// ...(get(unternehmen?.JahresUmsatz, 'max') !== undefined ?
				// 	[{ validateFunction: GetMaxDecimalValidation, params: [get(unternehmen?.JahresBilanzSumme, 'max'), '', errorMaxMessage] }]
				// :	[]),
			]
		:	[
				{ validateFunction: Required },
				// { validateFunction: GetMinDecimalValidation, params: [minMaxJahresBilanz?.property?.min, '', errorSelectedMinMessage] },
				// ...(!selectedJahresUmsatz || get(minMaxJahresBilanz?.property, 'max') !== undefined ?
				// 	[{ validateFunction: GetMaxDecimalValidation, params: [get(minMaxJahresBilanz?.property, 'max'), '', errorSelectedMaxMessage] }]
				// :	[{ validateFunction: NonNegativeDecimalValidation }]),
			];

	let struc: FormStrucItem[] = [
		{
			fields: [
				{
					...P.Selbsterklaerung_KMU.schema.attributes.Kmu_Kennzahlen.schema.attributes.Kmu_Kennzahlen_Bezugsjahr,
					validation: [
						{ validateFunction: Required },
						{ validateFunction: FullYear },
						// { validateFunction: GetMaxIntValidation, params: [currentYear] },
						// {
						// 	validateFunction: GetMinIntValidation,
						// 	params: [minyear, 'Bitte geben Sie ein gültiges Gründungsjahr unter "Identität des Unternehmens" ein.'],
						// },
						// ...(bezugsjahr ?
						// 	[
						// 		{
						// 			validateFunction: GetNotEqualIntValidation,
						// 			params: [bezugsjahr, '', 'Das Bezugsjahr wurde bereits verwendet.'],
						// 		},
						// 	]
						// :	[]),
					],
				},
				{
					...P.Selbsterklaerung_KMU.schema.attributes.Kmu_Kennzahlen.schema.attributes.Kmu_Kennzahlen_Mitarbeiterzahl,
					validation: mitarbeiterzahlValidation,
				},
				{
					...P.Selbsterklaerung_KMU.schema.attributes.Kmu_Kennzahlen.schema.attributes.Kmu_Kennzahlen_Jahresumsatz,
					validation: jahresumsatzValidation,
				},
				{
					...P.Selbsterklaerung_KMU.schema.attributes.Kmu_Kennzahlen.schema.attributes.Kmu_Kennzahlen_Jahresbilanzsumme,
					validation: jahresBilanzValidation,
				},
			],
			userProps: { columnToDivide: 1 },
		},
	];
	return struc;
};

export const getFormStrucAntragstellerKmuErklaerungGlobal = (application: any, unternehmenId: number, positionId?: string) => {
	let formStrucValidation: FormStrucItem[] = [];
	const antragsformular = get(application, P.Allgemein_Antragsformular.path, '');
	const isLoewe = antragsformular === ANTRAGS_FORMULAREN.Loewe;
	get(application, P.Selbsterklaerung_KMU.path, []).forEach((ele: any, i: number) => {
		if (get(ele, `${KMU_PATHS.Unternehmen}.id`, -1) === unternehmenId || get(ele, KMU_PATHS.PositionID, '') === positionId) {
			const isKMU = get(ele, KMU_PATHS.Einschaetzung_Als_Unternehmen, '') === EINSCHAETZUNG_ALS_UNTERNEHMEN.KMU;
			const isKeinKMU = get(ele, KMU_PATHS.Einschaetzung_Als_Unternehmen, '') === EINSCHAETZUNG_ALS_UNTERNEHMEN.KeinKMU;
			const isFamilienGefuehrtesUnternehmen =
				get(ele, KMU_PATHS.Unternehmenseinschaetzung_Fuer_Nicht_Kmus, '') === EINSCHAETZUNG_NICHT_KMU.familiengefuehrtesUnternehmen;
			const isInhaberGefuehrtesUnternehmen =
				get(ele, KMU_PATHS.Unternehmenseinschaetzung_Fuer_Nicht_Kmus, '') === EINSCHAETZUNG_NICHT_KMU.inhaberPersonGefuehrtesUnternehmen;

			const isHinweisFieldRequired = checkIfPruefAssitenzNeedsToValidate(ele, KMU_PATHS.Prufueng_Prufstatus, KMU_PATHS.Prufueng_Erledigt);

			let paramYear: number | undefined = undefined;
			let customFormStruc = [{}];

			get(ele, 'Kmu_Kennzahlen', []).forEach((_kmuKennzahl: any, kennzahlIndex: number) => {
				if (kennzahlIndex > 0) {
					const prevBezugsjahr = addIndexesToPath(
						P.Selbsterklaerung_KMU.schema.attributes.Kmu_Kennzahlen.schema.attributes.Kmu_Kennzahlen_Bezugsjahr.path,
						[`${i}`, `${kennzahlIndex - 1}`],
						['Selbsterklaerung_KMU', 'Kmu_Kennzahlen'],
					);
					paramYear = Number(get(application, prevBezugsjahr));
				}
				const customFormStrucMapped = mapFormStrucKennzahlen(customFormStrucKennzahlen(application, paramYear, i), i, kennzahlIndex);
				customFormStruc = [...customFormStruc, ...customFormStrucMapped];
			});

			const formStrucPruefAssistenzMapped = mapFormStruc(formStrucPruefAssistenz(), i);
			const formStrucVerzichtMapped = mapFormStruc(formStrucVerzicht(), i);
			const formStrucMapped = mapFormStruc(formStrucUnternehmensTyp(), i);
			const formStrucErklaerungenFuesionMapped = mapFormStruc(formStrucErklaerungenFuesion(), i);
			const formStrucErklaerungenKontrolleMapped = mapFormStruc(formStrucErklaerungenKontrolle(), i);
			const formStrucErklaerungenKapitalMapped = mapFormStruc(formStrucErklaerungenKapital(), i);
			const formStrucKriteriencMapped = mapFormStruc(formStrucKriterien(application), i);
			const formStrucUmsatzMapped = mapFormStruc(formStrucUmsatz(), i);
			const formStrucUploadErklaerungMapped = mapFormStruc(formStrucUploadErklaerung(), i);

			if (isKeinKMU && isLoewe) {
				const formStrucNichtKmuMapped = mapFormStruc(formStrucNichtKmu(), i);
				formStrucValidation = concat(formStrucValidation, formStrucNichtKmuMapped);
			}
			if (!(isTrue(isKMU) || (isKeinKMU && isFamilienGefuehrtesUnternehmen) || (isKeinKMU && isInhaberGefuehrtesUnternehmen))) {
				if (isHinweisFieldRequired) {
					formStrucValidation = concat(formStrucPruefAssistenzMapped);
				} else {
					formStrucValidation = concat(formStrucVerzichtMapped);
				}
			} else if (isHinweisFieldRequired) {
				formStrucValidation = concat(
					formStrucPruefAssistenzMapped,
					formStrucMapped,
					customFormStruc,
					formStrucErklaerungenFuesionMapped,
					formStrucErklaerungenKontrolleMapped,
					formStrucErklaerungenKapitalMapped,
					formStrucKriteriencMapped,
					formStrucUmsatzMapped,
					formStrucUploadErklaerungMapped,
				);
			} else {
				formStrucValidation = concat(
					formStrucMapped,
					customFormStruc,
					formStrucErklaerungenFuesionMapped,
					formStrucErklaerungenKontrolleMapped,
					formStrucErklaerungenKapitalMapped,
					formStrucKriteriencMapped,
					formStrucUmsatzMapped,
					formStrucUploadErklaerungMapped,
				);
			}
		}
	});

	return formStrucValidation;
};

const descriptions = {
	EigenstaendigesUnternehmen: `Eigenständige Unternehmen sind unabhängig, d.h. sie sind nicht an anderen Unternehmen beteiligt und keine anderen Unternehmen halten Anteile. Als eigenständiges Unternehmen gelten auch Unternehmen, die weniger als 25% des Kapitals oder der Stimmrechte an einem oder mehreren anderen Unternehmen halten, ebenso Unternehmen, an denen keine Anteile an Kapital oder Stimmrechten von 25% oder mehr gehalten werden. Bei den Anteilen gilt jeweils der höhere Wert.

Ein Unternehmen gilt weiterhin als eigenständig, wenn der Schwellenwert von 25% erreicht oder überschritten wird, sofern es sich um folgende Kategorien von Investoren handelt und unter der Bedingung, dass diese Investoren nicht einzeln oder gemeinsam mit dem betroffenen Unternehmen verbunden sind:

* staatliche Beteiligungsgesellschaften; Risikokapitalgesellschaften; natürliche Personen bzw. Gruppen natürlicher Personen, die regelmäßig im Bereich Risikokapitalinvestition tätig sind („Business Angels“) und die Eigenmittel in nicht börsennotierte Unternehmen investieren, sofern der Gesamtbetrag der Investition der genannten „Business Angels“ in das betroffene Unternehmen 1,25 Mio. EUR nicht überschreitet,
* Universitäten oder Forschungszentren ohne Gewinnzweck
* institutionelle Anleger einschließlich regionaler Entwicklungsfonds,
* autonome Gebietskörperschaften mit einem Jahreshaushalt von weniger als 10 Mio. EUR und weniger als 5.000 Einwohnern.

\nEiner oder mehrere der oben genannten Anleger dürfen einzeln einen Anteil von bis zu 50% an einem Unternehmen besitzen, sofern sie weder einzeln noch gemeinsam mit dem Unternehmen, dessen KMU-Status bewertet wird, verbunden sind.`,

	Partnerunternehmen: `Partnerunternehmen halten allein oder gemeinsam mit einem oder mehreren verbundenen Unternehmen 25% bis maximal 50% des Kapitals oder der Stimmrechte an einem anderen Unternehmen, und/oder ein anderes Unternehmen hält allein oder gemeinsam mit einem oder mehreren verbundenen Unternehmen einen Anteil von 25% bis maximal 50% des Kapitals oder der Stimmrechte an dem betroffenen Unternehmen.

Ein Unternehmen kann allerdings nicht als KMU angesehen werden, wenn 25% oder mehr seines Kapitals oder seiner Stimmrechte direkt oder indirekt von einer oder mehreren öffentlichen Stellen oder Körperschaften des öffentlichen Rechts einzeln oder gemeinsam kontrolliert werden. Die oben genannten Kategorien von Investoren, wie etwa Universitäten oder autonome Gebietskörperschaften, die nach nationalem Recht den Status einer öffentlich-rechtlichen Körperschaft innehaben, sind von dieser Regelung nicht betroffen. Der Gesamtanteil dieser Anleger kann sich auf bis zu 50% der Stimmrechte des betreffenden Unternehmens belaufen, ohne dass dieses den KMU-Status verliert.`,

	VerbundenesUnternehmen: `Verbundene Unternehmen sind Unternehmen, die zueinander in einer der folgenden Beziehungen stehen:

* ein Unternehmen ist verpflichtet, einen konsolidierten Jahresabschluss zu erstellen;,
* ein Unternehmen hält die Mehrheit der Stimmrechte der Aktionäre oder Gesellschafter eines anderen Unternehmens
* ein Unternehmen ist berechtigt, die Mehrheit der Mitglieder des Verwaltungs-, Leitungs- oder Aufsichtsgremiums eines anderen Unternehmens zu bestellen oder abzuberufen,
* ein Unternehmen ist gemäß einem mit einem anderen Unternehmen abgeschlossenen Vertrag oder aufgrund einer Klausel in dessen Satzung berechtigt, einen beherrschenden Einfluss auf dieses Unternehmen auszuüben,
* ein Unternehmen, das Aktionär oder Gesellschafter eines anderen Unternehmens ist, übt gemäß einer mit anderen Aktionären oder Gesellschaftern dieses anderen Unternehmens getroffenen Vereinbarung die alleinige Kontrolle über die Mehrheit der Stimmrechte von dessen Aktionären oder Gesellschaftern aus. 

\nEs besteht die Vermutung, dass kein beherrschender Einfluss ausgeübt wird, sofern sich die oben genannten Investoren nicht direkt oder indirekt in die Verwaltung des betrachteten Unternehmens einmischen – unbeschadet der Rechte, die sie in ihrer Eigenschaft als Aktionäre oder Gesellschafter besitzen.

Unternehmen, die durch ein oder mehrere andere Unternehmen untereinander oder durch einen der oben genannten Investoren in einer der oben genannten Beziehung stehen, gelten ebenfalls als verbunden.

Unternehmen, die durch eine natürliche Person oder eine gemeinsam handelnde Gruppe natürlicher Personen miteinander in einer dieser Beziehungen stehen, gelten gleichermaßen als verbundene Unternehmen, sofern diese Unternehmen ganz oder teilweise in demselben Markt oder in benachbarten Märkten tätig sind. Als benachbarter Markt gilt der Markt für ein Produkt oder eine Dienstleistung, der dem betreffenden Markt unmittelbar vor- oder nachgeschaltet ist.
`,
};

// function getSelectedMinMaxKMU(selectedValue: string) {
// 	return Object.entries(KMU_SELECTIONS.Institutionstyp)
// 		.flatMap(([institutionKey, institution]) =>
// 			Object.entries(institution).map(([propertyKey, property]) => ({
// 				institutionKey,
// 				propertyKey,
// 				property: typeof property === 'object' ? property : null,
// 			}))
// 		)
// 		.find(({ property }) => {
// 			return property && typeof property === 'object' && property.value === selectedValue;
// 		});
// }
